import { useState, useEffect, useCallback } from 'react'
import type { AppCore } from '@core/bootstrap'
import type { Product } from '@core/db/models'
import { syncService } from '@core/services/sync/SyncService'
import { money } from '@core/utils/format'
import { httpErrorText } from '@core/utils/httpError'
import { deviceLabel } from '@core/utils/deviceLabel'
import { useAuthToken } from './useAuthToken'
import { PosEvent } from '@core/bus/events'
import { CLOUD_API } from './shared'
import type { AdminPermissions } from './types'
import {
  btn,
  btnBase,
  btnSize,
  cx,
  hint,
  modalLg,
  overlay,
  table,
  tableWrap,
  td,
  tdNum,
  th,
  thNum
} from '@ui/classes'
import {
  badgeActive,
  badgeIncoming,
  badgeNo,
  badgeWeight,
  errBanner,
  filterBar,
  filterChip,
  filterChipActive,
  modInput,
  okBanner
} from '@ui/admin'
import { Alert, Check, Close, Pencil, Refresh, Scales, Trash } from '@ui/icons'

type PendingStatus = 'pending' | 'approved' | 'rejected'

interface CloudPending {
  id: string
  client_uuid: string
  device_id: string
  created_by_name: string | null
  name: string
  barcode: string
  price: string
  category: string
  unit: string
  is_weight: boolean
  requires_marking: boolean
  status: PendingStatus
  client_created_at: string | null
  created_at: string
  review_note: string | null
}

interface PendingForm {
  name: string
  price: string
  barcode: string
  category: string
  unit: string
  is_weight: boolean
  requires_marking: boolean
}

const STATUS_LABEL: Record<PendingStatus, string> = {
  pending: 'Ожидает',
  approved: 'Одобрена',
  rejected: 'Отклонена'
}

export function PosModerationSection({
  core,
  perms,
  cashier
}: {
  core: AppCore
  perms: AdminPermissions
  cashier?: { user_id: string } | null
}) {
  const { catalog } = core
  const token = useAuthToken()
  const canReview = perms.canEditCatalog

  const [statusFilter, setStatusFilter] = useState<PendingStatus | 'all'>('pending')
  const [online, setOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine
  )
  const [cloudItems, setCloudItems] = useState<CloudPending[]>([])
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 })
  const [cloudErr, setCloudErr] = useState<string | null>(null)
  const [cloudBusy, setCloudBusy] = useState(false)
  const [query, setQuery] = useState('')

  const [items, setItems] = useState<Product[]>([])
  const [edit, setEdit] = useState<
    Record<
      number,
      { name: string; price: string; barcode: string; stock: string; requiresMarking: boolean }
    >
  >({})
  const [note, setNote] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const [pendingEdit, setPendingEdit] = useState<CloudPending | null>(null)
  const [form, setForm] = useState<PendingForm | null>(null)
  const [duplicate, setDuplicate] = useState<{
    pendingId: string
    existing: { id: number; name: string; price: number }
  } | null>(null)

  const loadCloud = useCallback(async (silent = false) => {
    if (!token) return
    if (!silent) setCloudBusy(true)
    setCloudErr(null)
    try {
      const resp = await fetch(`${CLOUD_API}/catalog/pending?status=${statusFilter}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (resp.status === 404) {
        // Отдельный текст: 404 здесь означает, что на сервере нет метода
        // (старая сборка бэкенда), а не «заявок не найдено».
        setCloudErr(
          'Сервис облака не обновлён: раздел «Заявки с касс» недоступен. Нужен деплой актуальной версии бэкенда.'
        )
        return
      }
      if (!resp.ok) {
        setCloudErr(httpErrorText('Загрузка заявок', resp.status))
        return
      }
      const data = (await resp.json()) as {
        items: CloudPending[]
        counts: { pending: number; approved: number; rejected: number }
      }
      setCloudItems(data.items)
      setCounts(data.counts)
    } catch (e: unknown) {
      setCloudErr(e instanceof Error ? e.message : String(e))
    } finally {
      if (!silent) setCloudBusy(false)
    }
  }, [token, statusFilter])

  const reloadLocal = useCallback(() => {
    void catalog
      .listCreatedOnPosProducts()
      .then((list) => {
        setItems(list)
        const next: typeof edit = {}
        for (const p of list) {
          if (p.id != null) {
            next[p.id] = {
              name: p.name,
              price: String(p.price),
              barcode: p.barcode,
              stock: String(p.stock),
              requiresMarking: Boolean(p.requiresMarking)
            }
          }
        }
        setEdit(next)
      })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
  }, [catalog])

  useEffect(() => {
    reloadLocal()
  }, [reloadLocal])

  useEffect(() => {
    void loadCloud()
  }, [loadCloud])

  // Статусы заявок меняются в фоне (цикл синка) — обновляем экран без F5.
  useEffect(() => {
    const off = core.bus.on(PosEvent.PENDING_UPDATED, () => {
      reloadLocal()
      void loadCloud(true)
    })
    return off
  }, [core.bus, reloadLocal, loadCloud])

  // Страховка: если событие пропущено (синк ещё не стартовал), обновляем раз в 30 с.
  useEffect(() => {
    const id = window.setInterval(() => {
      reloadLocal()
      void loadCloud(true)
    }, 30_000)
    return () => window.clearInterval(id)
  }, [reloadLocal, loadCloud])

  useEffect(() => {
    const on = () => setOnline(navigator.onLine)
    window.addEventListener('online', on)
    window.addEventListener('offline', on)
    return () => {
      window.removeEventListener('online', on)
      window.removeEventListener('offline', on)
    }
  }, [])

  const approve = async (
    item: CloudPending,
    opts?: { merge?: boolean; overrides?: Partial<PendingForm> }
  ) => {
    if (!token || !canReview) return
    setCloudBusy(true)
    setCloudErr(null)
    try {
      const overrides = opts?.overrides
      const resp = await fetch(`${CLOUD_API}/catalog/pending/${item.id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          reviewed_by_user_id: cashier?.user_id ?? null,
          merge: Boolean(opts?.merge),
          overrides: overrides
            ? {
                name: overrides.name,
                price: Number(String(overrides.price).replace(',', '.')),
                barcode: overrides.barcode,
                category: overrides.category,
                unit: overrides.unit,
                is_weight: overrides.is_weight,
                requires_marking: overrides.requires_marking
              }
            : undefined
        })
      })
      if (resp.status === 409) {
        const body = (await resp.json().catch(() => ({}))) as {
          error?: string
          existing_product?: { id: number; name: string; price: number }
        }
        if (body.error === 'duplicate_barcode' && body.existing_product) {
          setDuplicate({ pendingId: item.id, existing: body.existing_product })
          return
        }
      }
      if (!resp.ok) {
        setCloudErr(httpErrorText('Одобрение заявки', resp.status))
        return
      }
      setNote(`Заявка «${item.name}» одобрена`)
      setPendingEdit(null)
      setForm(null)
      setDuplicate(null)
      await syncService.pullCatalog(token)
      await loadCloud()
      reloadLocal()
    } catch (e: unknown) {
      setCloudErr(e instanceof Error ? e.message : String(e))
    } finally {
      setCloudBusy(false)
    }
  }

  const reject = async (item: CloudPending) => {
    if (!token || !canReview) return
    setCloudBusy(true)
    setCloudErr(null)
    try {
      const resp = await fetch(`${CLOUD_API}/catalog/pending/${item.id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          reviewed_by_user_id: cashier?.user_id ?? null,
          note: 'Отклонено в кабинете'
        })
      })
      if (!resp.ok) {
        setCloudErr(httpErrorText('Отклонение заявки', resp.status))
        return
      }
      setNote(`Заявка «${item.name}» отклонена`)
      await loadCloud()
      reloadLocal()
    } catch (e: unknown) {
      setCloudErr(e instanceof Error ? e.message : String(e))
    } finally {
      setCloudBusy(false)
    }
  }

  const saveLocal = async (id: number) => {
    const row = edit[id]
    if (!row) return
    setErr(null)
    try {
      await catalog.updateProduct(id, {
        name: row.name,
        price: Number(row.price.replace(',', '.')),
        barcode: row.barcode,
        stock: Number(row.stock.replace(',', '.')),
        requiresMarking: row.requiresMarking
      })
      setNote('Товар обновлён')
      reloadLocal()
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }

  const removeLocal = async (id: number) => {
    setErr(null)
    try {
      await catalog.deleteProduct(id)
      setNote('Товар удалён (мягко)')
      reloadLocal()
      await loadCloud()
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }

  const openEdit = (item: CloudPending) => {
    setPendingEdit(item)
    setForm({
      name: item.name,
      price: String(item.price),
      barcode: item.barcode,
      category: item.category,
      unit: item.unit,
      is_weight: item.is_weight,
      requires_marking: item.requires_marking
    })
  }

  const filteredCloud = cloudItems.filter((item) => {
    const q = query.trim().toLowerCase()
    if (!q) return true
    return item.name.toLowerCase().includes(q) || item.barcode.toLowerCase().includes(q)
  })

  const localStatus = (p: Product): string => {
    if (!p.cloudSyncedAt) return p.cloudStatus === 'rejected' ? 'Отклонён' : 'Ожидает отправки'
    if (p.cloudStatus === 'approved') return 'Одобрен'
    if (p.cloudStatus === 'rejected') return 'Отклонён'
    return 'На модерации'
  }


  return (
    <div>
      {note && <p className={okBanner}>{note}</p>}
      {err && <p className={errBanner}>{err}</p>}

      {!token ? (
        <p className={hint}>
          Одобрение требует связи с облаком — подключите облако в «Интеграциях».
          Локальные товары этого терминала доступны ниже.
        </p>
      ) : (
        <>
          {!online && (
            <p className={hint}>
              Офлайн: одобрение требует связи с облаком. Локальная модерация работает.
            </p>
          )}
          <div className={filterBar}>
            {(['pending', 'approved', 'rejected', 'all'] as const).map((key) => {
              const label =
                key === 'all'
                  ? 'Все'
                  : key === 'pending'
                    ? `Ожидают (${counts.pending})`
                    : key === 'approved'
                      ? `Одобрены (${counts.approved})`
                      : `Отклонены (${counts.rejected})`
              return (
                <button
                  key={key}
                  type="button"
                  className={statusFilter === key ? filterChipActive : filterChip}
                  onClick={() => setStatusFilter(key)}
                >
                  {label}
                </button>
              )
            })}
            <button
              type="button"
              className={cx(btnBase, btn.secondary, btnSize.sm)}
              onClick={() => void loadCloud()}
              disabled={cloudBusy || !online}
            >
              <Refresh size={16} /> Обновить
            </button>
          </div>

          <div className="mb-3">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск по наименованию или ШК…"
              className={cx(modInput, 'w-full max-w-[320px]')}
            />
          </div>

          {cloudErr && (
            <div className="my-2 flex items-center justify-between gap-2 rounded-card border border-warnLine bg-red-50 px-3 py-2">
              <span className="text-small text-dangerDark">{cloudErr}</span>
              <button
                className={cx(btnBase, btn.secondary, btnSize.sm)}
                onClick={() => void loadCloud()}
              >
                Повторить
              </button>
            </div>
          )}

          {duplicate && (
            <div className="my-2 flex flex-wrap items-center justify-between gap-2 rounded-card border border-amber-300 bg-amber-50 px-3 py-2 text-small text-amber-800">
              <span>
                Товар с таким ШК уже есть: <strong>{duplicate.existing.name}</strong> —{' '}
                {money(duplicate.existing.price)}. Обновить его данными заявки?
              </span>
              <button
                className={cx(btnBase, btn.secondary, btnSize.sm)}
                onClick={() => {
                  const item = cloudItems.find((c) => c.id === duplicate.pendingId)
                  if (!item) return
                  const overrides =
                    pendingEdit?.id === duplicate.pendingId && form ? form : undefined
                  void approve(item, { merge: true, overrides })
                }}
              >
                Обновить существующий
              </button>
            </div>
          )}


          {filteredCloud.length === 0 ? (
            <p className={hint}>
              {query.trim()
                ? 'Ничего не найдено по запросу.'
                : 'Заявок нет — товары, созданные на кассах, появятся здесь.'}
            </p>
          ) : (
            <div className={tableWrap}>
              <table className={table}>
                <thead>
                  <tr>
                    <th className={th}>Товар</th>
                    <th className={th}>ШК</th>
                    <th className={thNum}>Цена</th>
                    <th className={th}>Категория</th>
                    <th className={th}>Терминал</th>
                    <th className={th}>Автор</th>
                    <th className={th}>Создано</th>
                    <th className={th}>Статус</th>
                    {statusFilter === 'pending' && <th className={th}>Действия</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredCloud.map((item) => (
                    <tr key={item.id}>
                      <td className={td}>
                        {item.name}
                        {item.is_weight && (
                          <span className={cx(badgeWeight, 'ml-1.5')}>
                            <Scales size={16} /> кг
                          </span>
                        )}
                      </td>
                      <td className={td}>{item.barcode || '—'}</td>
                      <td className={tdNum}>{money(Number(item.price))}</td>
                      <td className={td}>{item.category}</td>
                      <td className={cx(td, 'whitespace-nowrap')}>
                        {deviceLabel(item.device_id).short}
                      </td>
                      <td className={td}>{item.created_by_name ?? '—'}</td>
                      <td className={cx(td, 'whitespace-nowrap')}>
                        {new Date(item.created_at).toLocaleString('ru-RU')}
                      </td>
                      <td className={td}>
                        <span
                          className={
                            item.status === 'pending'
                              ? badgeIncoming
                              : item.status === 'approved'
                                ? badgeActive
                                : badgeNo
                          }
                        >
                          {STATUS_LABEL[item.status]}
                        </span>
                      </td>
                      {statusFilter === 'pending' && (
                        <td className={cx(td, 'whitespace-nowrap')}>
                          {canReview ? (
                            <>
                              <button
                                className={cx(btnBase, btn.success, btnSize.sm, 'mr-1')}
                                onClick={() => void approve(item)}
                                disabled={cloudBusy || !online}
                              >
                                <Check size={16} /> Одобрить
                              </button>
                              <button
                                className={cx(btnBase, btn.secondary, btnSize.sm, 'mr-1')}
                                onClick={() => openEdit(item)}
                                disabled={cloudBusy || !online}
                              >
                                <Pencil size={16} /> Одобрить с правкой
                              </button>
                              <button
                                className={cx(btnBase, btn.dangerGhost, btnSize.sm)}
                                onClick={() => void reject(item)}
                                disabled={cloudBusy || !online}
                              >
                                <Close size={16} /> Отклонить
                              </button>
                            </>
                          ) : (
                            <span className={hint}>Нет права одобрения</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}



      <h4 className="mb-2 mt-4 text-base2 font-bold">Товары этого терминала</h4>
      <p className={hint}>
        Созданные на этой кассе товары. До одобрения продаются только здесь.
      </p>
      {items.length === 0 ? (
        <p className={hint}>Нет товаров, заведённых кассирами на лету.</p>
      ) : (
        <div className={tableWrap}>
          <table className={table}>
            <thead>
              <tr>
                <th className={th}>Штрихкод</th>
                <th className={th}>Наименование</th>
                <th className={th}>Категория</th>
                <th className={thNum}>Цена</th>
                <th className={thNum}>Остаток</th>
                <th className={th}>Маркировка</th>
                <th className={th}>Статус</th>
                <th className={th}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => {
                const row = p.id != null ? edit[p.id] : undefined
                if (p.id == null || !row) return null
                const id = p.id
                const readOnly = p.cloudStatus === 'approved' || p.cloudStatus === 'rejected'
                return (
                  <tr key={id}>
                    <td className={td}>
                      <input
                        value={row.barcode}
                        disabled={readOnly}
                        onChange={(e) =>
                          setEdit((s) => ({ ...s, [id]: { ...s[id], barcode: e.target.value } }))
                        }
                        className={modInput}
                      />
                    </td>
                    <td className={td}>
                      <input
                        value={row.name}
                        disabled={readOnly}
                        onChange={(e) =>
                          setEdit((s) => ({ ...s, [id]: { ...s[id], name: e.target.value } }))
                        }
                        className={cx(modInput, 'min-w-[140px]')}
                      />
                    </td>
                    <td className={td}>
                      {p.category || 'Общее'}
                      {(p.isWeight || p.unit === 'кг') && (
                        <span className={cx(badgeWeight, 'ml-1.5')}>
                          <Scales size={16} /> кг
                        </span>
                      )}
                    </td>
                    <td className={tdNum}>
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={row.price}
                        disabled={readOnly}
                        onChange={(e) =>
                          setEdit((s) => ({ ...s, [id]: { ...s[id], price: e.target.value } }))
                        }
                        className={cx(modInput, 'w-[90px] text-right')}
                      />
                    </td>
                    <td className={tdNum}>
                      <input
                        type="number"
                        min={0}
                        step="0.001"
                        value={row.stock}
                        disabled={readOnly}
                        onChange={(e) =>
                          setEdit((s) => ({ ...s, [id]: { ...s[id], stock: e.target.value } }))
                        }
                        className={cx(modInput, 'w-20 text-right')}
                      />
                    </td>
                    <td className={td}>
                      <input
                        type="checkbox"
                        checked={row.requiresMarking}
                        disabled={readOnly}
                        onChange={(e) =>
                          setEdit((s) => ({
                            ...s,
                            [id]: { ...s[id], requiresMarking: e.target.checked }
                          }))
                        }
                        className="h-4 w-4 cursor-pointer accent-ok"
                      />
                    </td>
                    <td className={td}>
                      <span
                        className={
                          localStatus(p) === 'Одобрен'
                            ? badgeActive
                            : localStatus(p) === 'Отклонён'
                              ? badgeNo
                              : badgeIncoming
                        }
                      >
                        {localStatus(p)}
                      </span>
                    </td>
                    <td className={cx(td, 'whitespace-nowrap')}>
                      {!readOnly && (
                        <>
                          <button
                            onClick={() => void saveLocal(id)}
                            className={cx(btnBase, btn.secondary, btnSize.sm, 'mr-1')}
                          >
                            Сохранить
                          </button>
                          <button
                            onClick={() => void removeLocal(id)}
                            className={cx(btnBase, btn.dangerGhost, btnSize.sm)}
                          >
                            <Trash size={16} /> Удалить
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {pendingEdit && form && (
        <div className={cx(overlay, 'z-modal')} onClick={() => !cloudBusy && setPendingEdit(null)}>
          <div className={modalLg} onClick={(e) => e.stopPropagation()}>
            <h4 className="mt-0 text-base2 font-bold">Изменить и одобрить</h4>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-small">
                Наименование
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => (f ? { ...f, name: e.target.value } : f))}
                  className={modInput}
                />
              </label>
              <label className="flex flex-col gap-1 text-small">
                Штрихкод
                <input
                  value={form.barcode}
                  onChange={(e) => setForm((f) => (f ? { ...f, barcode: e.target.value } : f))}
                  className={modInput}
                />
              </label>
              <label className="flex flex-col gap-1 text-small">
                Цена
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.price}
                  onChange={(e) => setForm((f) => (f ? { ...f, price: e.target.value } : f))}
                  className={modInput}
                />
              </label>
              <label className="flex flex-col gap-1 text-small">
                Категория
                <input
                  value={form.category}
                  onChange={(e) => setForm((f) => (f ? { ...f, category: e.target.value } : f))}
                  className={modInput}
                />
              </label>
              <label className="flex flex-col gap-1 text-small">
                Единица
                <select
                  value={form.unit}
                  onChange={(e) => setForm((f) => (f ? { ...f, unit: e.target.value } : f))}
                  className={modInput}
                >
                  <option value="шт">шт</option>
                  <option value="кг">кг</option>
                </select>
              </label>
              <div className="flex flex-col gap-2 pt-5 text-small">
                <label className="inline-flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={form.is_weight}
                    onChange={(e) =>
                      setForm((f) => (f ? { ...f, is_weight: e.target.checked } : f))
                    }
                    className="h-4 w-4 cursor-pointer accent-ok"
                  />
                  Весовой
                </label>
                <label className="inline-flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={form.requires_marking}
                    onChange={(e) =>
                      setForm((f) => (f ? { ...f, requires_marking: e.target.checked } : f))
                    }
                    className="h-4 w-4 cursor-pointer accent-ok"
                  />
                  Требует маркировки
                </label>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                className={cx(btnBase, btn.success, btnSize.md)}
                disabled={cloudBusy || !online}
                onClick={() => void approve(pendingEdit, { overrides: form })}
              >
                <Check size={18} /> Одобрить
              </button>
              <button
                className={cx(btnBase, btn.secondary, btnSize.md)}
                disabled={cloudBusy}
                onClick={() => setPendingEdit(null)}
              >
                Отмена
              </button>
              {duplicate && (
                <span className="inline-flex items-center gap-1.5 text-small text-amber-700">
                  <Alert size={16} /> ШК уже занят
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

